import { useMutation } from '@tanstack/react-query';
import { T, useTranslate } from '@tolgee/react';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import AppreciateSvg from '../../../../assets/icons/AppreciateSvg';
import TestPrepModalSvg from '../../../../assets/icons/TestPrepModalSvg';
import theme from '../../../../assets/theme';
import { LicenseCoarseLevels, UserAccessBadge } from '../../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useMetrics } from '../../../../hooks/useMetrics';
import { PaywallMetadata } from '../../../../hooks/usePaywallPrompt';
import { TestPrepAction, TestPrepState } from '../../../../hooks/useTestPrepState';
import BackendService from '../../../../services/backendService/backendService';
import { Input } from '../../../_atoms/_Input';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import BaseModal from '../../../_molecules/BaseModal';
import {
  CascadingFloatingSelect,
  TestPrepFloatingSelect,
} from '../components/FloatingSelect';
import { ConsumerGenerator } from './consumer-generator';
import {
  getQuestionTypes,
  getQuestionTypes_templated,
  useAllTestsForTestGroup,
  useGroupedStatesWithTests,
} from './TestPrep.Utils';
import { FormContainer, FormErrorText, OrangeButton } from './TestPrepTab';

export function TestPrepFirstPage({
  testPrepState,
  dispatch,
  tenantId,
  isUsTeacher,
  availableGenerators,
  userAccessBadge,
  generators,
  promptPaywall,
  isReady,
}: {
  testPrepState: TestPrepState;
  dispatch: React.Dispatch<TestPrepAction>;
  tenantId: string;
  isUsTeacher: boolean;
  availableGenerators: ConsumerGenerator[] | undefined;
  userAccessBadge: UserAccessBadge | undefined;
  generators: ConsumerGenerator[] | undefined;
  promptPaywall: (metadata?: PaywallMetadata | undefined) => void;
  isReady: boolean;
}) {
  const { t } = useTranslate();
  const metrics = useMetrics();
  const isTemplatedTestPrep = useFeatureFlag('templated_customizable_lessons');
  const [showRequestTestModal, setShowRequestTestModal] = useState<string | undefined>(
    undefined,
  );
  const [isFormErrorVisible, setIsFormErrorVisible] = useState(false);
  const [testRequest, setTestRequest] = useState<{
    state: string | undefined;
    nameOfTest: string | undefined;
  }>({ state: undefined, nameOfTest: undefined });
  const {
    selectedState,
    selectedTest,
    selectedTestCategory,
    selectedTestCategoryDropdown,
    selectedQuestionType,
    selectedPage,
    hasUploadOptions,
  } = testPrepState;

  const { mutate: submit, isLoading, isSuccess } = useMutation<
    void,
    string,
    { state: string; nameOfTest: string }
  >({
    mutationFn: ({ state, nameOfTest }: { state: string; nameOfTest: string }) => {
      const testNameLocationAppended = `${nameOfTest} \n*Dropdown requested from:* ${showRequestTestModal}`;

      return BackendService.instance.testRequest.submit(
        state,
        testNameLocationAppended,
        tenantId,
      );
    },
  });

  const groupedStatesWithTests = useGroupedStatesWithTests();

  const handleSelectTest = (state: string, test?: string) => {
    setIsFormErrorVisible(false);
    dispatch({ type: 'SET_SELECTED_STATE', payload: state });
    if (test) dispatch({ type: 'SET_SELECTED_TEST_CATEGORY', payload: test });
  };

  const handleLessonPageChange = (page: number) => {
    dispatch({ type: 'SET_SELECTED_PAGE', payload: page });
  };

  const isDesktop = useBreakPoint(700);
  const isLargerThan850 = useBreakPoint(850);

  const handleSendTestRequest = () => {
    if (!testRequest.state || !testRequest.nameOfTest) return;
    metrics.logEvent('TestPrep.RequestModalSubmitted', {
      state: testRequest.state,
      nameOfTest: testRequest.nameOfTest,
    });
    submit({ state: testRequest.state, nameOfTest: testRequest.nameOfTest });
  };

  const handleSelectedCategoryDropdown = (value: string | undefined) => {
    setIsFormErrorVisible(false);
    dispatch({ type: 'SET_SELECTED_TEST_CATEGORY_DROPDOWN', payload: value });
  };

  const availableQuestionTypes = useMemo(() => {
    return isTemplatedTestPrep
      ? getQuestionTypes_templated({
          testId: selectedTest,
          generators: availableGenerators,
          userAccessBadge,
        })
      : getQuestionTypes({
          testId: selectedTest,
          generators: availableGenerators,
          userAccessBadge,
        });
  }, [isTemplatedTestPrep, selectedTest, availableGenerators, userAccessBadge]);

  const availableTests = useAllTestsForTestGroup(
    selectedState,
    selectedTestCategory,
    generators,
    userAccessBadge,
  );

  const handleShowRequestTestModal = (location: string) => {
    setShowRequestTestModal(location);
    metrics.logEvent('TestPrep.RequestModalOpened', { location });
  };

  return (
    <>
      <BaseModal
        visible={!!showRequestTestModal}
        onCancel={() => {
          metrics.logEvent('TestPrep.RequestModalClosed');
          setShowRequestTestModal(undefined);
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          {!isSuccess && (
            <>
              <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
                <TestPrepModalSvg />
              </IconWrapper>
              <h1 style={{ marginTop: 20, fontSize: 24 }}>
                Let us know what test you are looking for
              </h1>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 30,
                }}
              >
                <Input
                  style={{ width: '100%' }}
                  type="text"
                  borderLess
                  inputStyle={{
                    boxShadow: theme.effects.floatingCreatorElementShadow,
                    border: 'none',
                  }}
                  value={testRequest?.state}
                  label={t('State', 'State')}
                  placeholder={t('e.g. Texas', 'e.g. Texas')}
                  labelStyle={{
                    fontSize: '18px',
                    color: theme.colors.black,
                    fontWeight: 'bold',
                  }}
                  onChange={e =>
                    setTestRequest({
                      ...testRequest,
                      state: e.target.value,
                    })
                  }
                />
                <Input
                  style={{ width: '100%' }}
                  type="text"
                  borderLess
                  inputStyle={{
                    boxShadow: theme.effects.floatingCreatorElementShadow,
                    border: 'none',
                  }}
                  value={testRequest?.nameOfTest}
                  label={t('Test', 'Test')}
                  placeholder={t(
                    'e.g. STAAR Science - Short constructed response',
                    'e.g. STAAR Science - Short constructed response',
                  )}
                  labelStyle={{
                    fontSize: '18px',
                    color: theme.colors.black,
                    fontWeight: 'bold',
                  }}
                  onChange={e =>
                    setTestRequest({
                      ...testRequest,
                      nameOfTest: e.target.value,
                    })
                  }
                />
              </div>
              <Button
                styles={{ marginTop: 20 }}
                loading={isLoading}
                onClick={() => handleSendTestRequest()}
                disabled={!testRequest.state || !testRequest.nameOfTest}
              >
                <T keyName="Send Request" defaultValue="Send Request" />
              </Button>
            </>
          )}
          {isSuccess && (
            <>
              <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
                <AppreciateSvg />
              </IconWrapper>
              <h1 style={{ marginTop: 20, fontSize: 24 }}>Thanks for your request!</h1>
              <p style={{ margin: 20, fontSize: 18 }}>
                Our team is adding your request to our list. In the meantime, explore
                available{' '}
                <Link href={`/${tenantId}/create?lessonCategory=test_prep`} passHref>
                  <StyledLink
                    onClick={() => metrics.logEvent('TestPrep.ViewAllTestPrep.Clicked')}
                  >
                    <T
                      keyName="test prep lessons here"
                      defaultValue="test prep lessons here"
                    />
                  </StyledLink>
                </Link>
                .
              </p>
            </>
          )}
        </div>
      </BaseModal>
      <FormContainer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr',
              alignItems: isDesktop ? 'center' : 'start',
              gap: 20,
              flexDirection: isDesktop ? 'row' : 'column',
            }}
          >
            <TestPrepFloatingSelect
              label={t('State', 'State')}
              width={isDesktop ? '100%' : '100%'}
              value={selectedState}
              options={groupedStatesWithTests.map(state => ({
                displayName: state.option.displayName,
                value: state.option.value,
                disabled: state.option.disabled,
                icon: state.option.icon,
                isEnterprise: state.option.isEnterprise,
                displayDivider: state.option.displayDivider,
              }))}
              onSelect={state => dispatch({ type: 'SET_SELECTED_STATE', payload: state })}
              displayCantFindOption={isUsTeacher ? true : false} //only give this option to US users
            />
            <TestPrepFloatingSelect
              label={t('Test', 'Test')}
              width={isDesktop ? '100%' : '100%'}
              value={selectedTestCategory}
              options={
                groupedStatesWithTests.find(state => state.option.value === selectedState)
                  ?.subOptions ?? []
              }
              onSelect={testCategory =>
                dispatch({ type: 'SET_SELECTED_TEST_CATEGORY', payload: testCategory })
              }
              displayCantFindOption={isUsTeacher ? true : false} //only give this option to US users
            />
          </div>
          <TestPrepFloatingSelect
            label={t('Subject', 'Subject')}
            width={isLargerThan850 ? undefined : '100%'}
            value={selectedTest}
            options={availableTests}
            onSelect={test => dispatch({ type: 'SET_SELECTED_TEST', payload: test })}
            displayCantFindOption={isUsTeacher ? true : false} //only give this option to US users
            handleShowRequestTestModal={(location: string) =>
              handleShowRequestTestModal(location)
            }
            promptPaywall={promptPaywall}
            userAccessBadge={userAccessBadge}
            isReady={isReady}
            disabled={!selectedState}
            testRequest={
              <span style={{ fontWeight: 'normal', color: theme.colors.greyDark }}>
                <T
                  keyName="Don't see your subject?"
                  defaultValue="Don't see your subject?"
                />{' '}
                <i
                  style={{
                    color: theme.colors.greyDark,
                    textDecoration: 'underline',
                  }}
                >
                  <T
                    keyName="Request your subject here."
                    defaultValue="Request your subject here."
                  />
                </i>
              </span>
            }
          />
          <TestPrepFloatingSelect
            label={t('Question Type', 'Question Type')}
            width={isLargerThan850 ? undefined : '100%'}
            value={selectedQuestionType}
            options={availableQuestionTypes}
            onSelect={questionType => {
              dispatch({ type: 'SET_SELECTED_QUESTION_TYPE', payload: questionType });
            }}
            displayCantFindOption={isUsTeacher ? true : false} //only give this option to US users
            handleShowRequestTestModal={(location: string) =>
              handleShowRequestTestModal(location)
            }
            promptPaywall={promptPaywall}
            userAccessBadge={userAccessBadge}
            isReady={isReady}
            disabled={!selectedState}
            testRequest={
              <span style={{ fontWeight: 'normal', color: theme.colors.greyDark }}>
                <T
                  keyName="Don't see the question type you're looking for?"
                  defaultValue="Don't see the question type you're looking for?"
                />{' '}
                <i
                  style={{
                    color: theme.colors.greyDark,
                    textDecoration: 'underline',
                  }}
                >
                  <T keyName="Request it here." defaultValue="Request it here." />
                </i>
              </span>
            }
          />
        </div>

        <div
          style={{
            margin: '20px auto 10px auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <OrangeButton
            type="primary"
            width={isDesktop ? '200px' : '100%'}
            onClick={() => {
              if (!selectedState || !selectedTest || !selectedQuestionType) {
                setIsFormErrorVisible(true);
                return;
              }

              metrics.logEvent('TestPrep.NextButtonClicked', {
                state: selectedState,
                test: selectedTest,
                questionType: selectedQuestionType,
                currentPage: 1,
              });

              const hasAccessToEnterprise =
                userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise;

              const isEnterpriseQuestionOptionSelected = availableQuestionTypes.find(
                q => q.value === selectedQuestionType,
              )?.isEnterprise;

              const isEnterpriseTestSelected = availableTests.find(
                t => t.value === selectedTest,
              )?.isEnterprise;

              if (
                (isEnterpriseQuestionOptionSelected || isEnterpriseTestSelected) &&
                !hasAccessToEnterprise
              ) {
                promptPaywall({
                  label: t('Upgrade account test prep', 'Upgrade account test prep'),
                  description: t(
                    'Access to ${selectedTest} ${selectedQuestionType}',
                    'Access to ${selectedTest} ${selectedQuestionType}',
                    {
                      selectedTest,
                      selectedQuestionType,
                    },
                  ),
                });
                return;
              }

              if (!hasUploadOptions) {
                //If there are no upload options then we skip page 2
                handleLessonPageChange(3);
                return;
              }

              handleLessonPageChange(selectedPage + 1);
            }}
            $disabled={false}
          >
            Next
          </OrangeButton>
        </div>
        {isFormErrorVisible && (
          <FormErrorText>
            <T
              keyName="Don't forget to select a test"
              defaultValue="Don't forget to select a test"
            />
          </FormErrorText>
        )}
      </FormContainer>
    </>
  );
}

const StyledLink = styled.a`
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`;
